import { Component, OnInit, ViewChild, ElementRef, Input } from '@angular/core';
import { SnapshotService } from '../Services/snapshot.service';
import {CustomsnapshotService} from '../Services/customsnapshot.service';
import { SnapshotSharedVariablesService } from '../Services/snapshot-shared-variables.service';
import { Subject, takeUntil } from 'rxjs';
import { AlertMessageService } from 'src/app/common/services/alert-message.service';
import { AlertMessageModel } from 'src/app/common/models/common.model';

export interface MetricOption {
  Name: string;
  responseCode: string;
  isActive: boolean;
}

export interface Level2Config {
  Parent: string;
  MetricOptions: MetricOption[];
}

export interface EntryPointConfig {
  Level1: string | null;
  Level2: Level2Config[];
  variableCode: string | null;
}

export interface CrossTabConfiguration {
  EntryPoint1: EntryPointConfig | null;
  EntryPoint2: EntryPointConfig | null;
  EntryPoint3: EntryPointConfig | null;
  clientName: string | null;
  mandatory_variable_mapping: any[] | null;
  dimension: any[] | null;
  studyName: string | null;
  isMandatoryLoadClicked: boolean;
  snapshotType: string;
}

export interface TopicsRow {
  rowId: string;
  selected: boolean;
  snapshotType: string;
  sectionName: string;
  subSectionName: string[];
  entryPoint1: string[];
  entryPoint2: string[];
  entryPoint3: string[];
  brandList: string[];
  dimension1: string;
  dimension2: string;
  dimension3: string;
}

@Component({
  selector: 'app-topics-configuration',
  templateUrl: './topics-configuration.component.html',
  styleUrls: ['./topics-configuration.component.css']
})

export class TopicsConfigurationComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @Input() snapshot_details: any;
  isLoading = false;
  mandatoryVariableMapping: any=[];
  private destroy$ = new Subject<void>();
  dimensionList: any;
  hasSnapshotConfiguration: boolean = false;

  constructor(
    private _configService: SnapshotService,
    private _customConfigService: CustomsnapshotService,
    private clientDetails: SnapshotSharedVariablesService,
    private _alertService: AlertMessageService 
  ) {}

  ngOnInit(): void {
    const client = this.clientDetails.clientDetails.client_name;
    const study  = this.clientDetails.clientDetails.selectedStudy;
    if (!client || !study) return;
    this.initialLoad();
    this.loadCustomSnapshotConfiguration();
  }

  loadCustomSnapshotConfiguration(): void {
    const clientName = this.clientDetails.clientDetails.client_name;
    const snapshotName = this.clientDetails.clientDetails.snapshotselected;

    if (!clientName || !snapshotName) return;

    this._customConfigService.getCustomSnapshotWidgetLibrary({
      ClientName: clientName,
      SnapshotName: snapshotName
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        try {
          const data = typeof response.Data === 'string' ? JSON.parse(response.Data) : response.Data;
          this.hasSnapshotConfiguration = !!data && Object.keys(data).length > 0;
          
          // Load topics configuration only if snapshot is configured
          if (this.hasSnapshotConfiguration) {
            this.loadTopicsConfiguration();
          }
        } catch (error) {
          console.error('Error parsing snapshot configuration:', error);
          this.hasSnapshotConfiguration = false;
        }
      },
      error: (err: any) => {
        console.error('Load custom snapshot configuration error:', err);
        this.hasSnapshotConfiguration = false;
      }
    });
  }

  loadTopicsConfiguration(): void {
    const clientName = this.clientDetails.clientDetails.client_name;
    const studyName  = this.clientDetails.clientDetails.selectedStudy;
    const snapshotName = this.clientDetails.clientDetails.snapshotselected;

    this._configService.getTopicsConfiguration(clientName, studyName, snapshotName).subscribe({
      next: (response: any) => {
        const data =
          typeof response.Data === 'string'
            ? JSON.parse(response.Data)
            : response.Data;

        if (!data?.rows?.length) return;

        this.rows = data.rows.map((r: any): TopicsRow => ({
          rowId:          r.rowId,
          selected:       false,
          snapshotType:   this.snapshotTypeOptions.find(
                            o => o.value === r.snapshotType || o.label === r.snapshotType
                          )?.value ?? r.snapshotType,
          sectionName:    Array.isArray(r.sectionName) ? (r.sectionName[0] ?? '') : (r.sectionName ?? ''),
          subSectionName: Array.isArray(r.subSectionName) ? r.subSectionName : (r.subSectionName ? [r.subSectionName] : []),
          brandList:      r.brandList      || [],
          dimension1:     r.dimension1     || '',
          dimension2:     r.dimension2     || '',
          dimension3:     r.dimension3     || '',
          entryPoint1: r.EntryPoint1?.Level2?.[0]?.MetricOptions?.map(
            (m: any) => m.responseCode) || [],
          entryPoint2: r.EntryPoint2?.Level2?.[0]?.MetricOptions?.map(
            (m: any) => m.responseCode) || [],
          entryPoint3: r.EntryPoint3?.Level2?.[0]?.MetricOptions?.map(
            (m: any) => m.responseCode) || []
        }));

  this.rows.forEach((row, i) => {

    const restoredLevel1Values = [
      data.rows[i].EntryPoint1?.Level1 || null,
      data.rows[i].EntryPoint2?.Level1 || null,
      data.rows[i].EntryPoint3?.Level1 || null
    ];
    this.rowLevel1Values[i] = restoredLevel1Values;

    const snapshotLabel = this.snapshotTypeOptions.find(o => o.value === row.snapshotType)?.label ?? row.snapshotType;

    const brandCallback = (row.entryPoint1.length || row.entryPoint2.length || row.entryPoint3.length)
      ? () => this.restoreBrandOptions(i, restoredLevel1Values)
      : undefined;

    this.onSnapshotTypeChange(snapshotLabel, i, true);

    setTimeout(() => {
      this.onSectionChange(row.sectionName, i, true, brandCallback);

      const subSections = row.subSectionName.length ? row.subSectionName : [''];
      subSections.forEach((subSection, idx) => {
        setTimeout(() => {
          this.onSubSectionChange(subSection, i, true, idx === subSections.length - 1 ? brandCallback : undefined);
        }, (idx + 1) * 1000);
      });
    }, 1000);
  });

        
      },
      error: (err: any) => console.error('Load topics error:', err)
    });
  }

  selectedBrand = 'default';

  brandOptions = [
    { label: 'Default Brands', value: 'default' },
  ];

  snapshotTypeOptions = [
    { label: 'Multi Brand', value: 'multiBrand' },
    { label: 'Single Brand', value: 'singleBrand' },
    { label: 'Multi Category', value: 'multiCategory' },
    { label: 'Multi Market', value: 'multiMarket' },
    { label: 'Audience Snapshot', value: 'audienceSnapshot' },
  ];

  multiSelectOptions = [
    { label: 'Multiple', value: 'multiple' },
    { label: 'Single', value: 'single' },
  ];

  rows: TopicsRow[] = [
    {
      rowId: crypto.randomUUID(),
      selected: true,
      snapshotType: '',
      sectionName: '',
      subSectionName: [],
      entryPoint1: [],
      entryPoint2: [],
      entryPoint3: [],
      dimension1: '',
      dimension2: '',
      dimension3: '',
      brandList: [],
    }
  ];

  openDropdownIndex = -1;
  epOpenState: { [key: string]: boolean } = {};
  dropdownSearch: { [key: string]: string } = {};

  toggleEpDropdown(i: number, field: string): void {
    const key = `${i}_${field}`;
    const willOpen = !this.epOpenState[key];
    this.epOpenState[key] = willOpen;
    if (willOpen) { this.dropdownSearch[key] = ''; }
  }

  closeEpDropdown(i: number, field: string): void {
    const key = `${i}_${field}`;
    this.epOpenState[key] = false;
  }

  isEpOpen(i: number, field: string): boolean {
    return !!this.epOpenState[`${i}_${field}`];
  }

  filterOptions(items: any[], search: string): any[] {
    if (!search?.trim()) return items;
    const term = search.trim().toLowerCase();
    return items.filter(item => {
      const str = typeof item === 'string' ? item : (item.label ?? '');
      return str.toLowerCase().includes(term);
    });
  }

  getEpLabel(values: string[], options: Array<string | { label: string; value: string }> = []): string {
    return this.getSelectedValuesLabel(values, options);
  }

  getBrandLabel(values: string[], options: Array<string | { label: string; value: string }> = []): string {
    return this.getSelectedValuesLabel(values, options);
  }

  getSelectedValuesLabel(values: string[], options: Array<string | { label: string; value: string }> = []): string {
    if (!values?.length) return 'Select';
    if (this.areAllOptionsSelected(values, options)) return 'All';
    return values.length > 1 ? 'Multiple' : this.getOptionLabel(values[0], options);
  }

  getSelectedValuesTooltip(values: string[], options: Array<string | { label: string; value: string }> = []): string {
    if (!values?.length) return 'Select';
    if (this.areAllOptionsSelected(values, options)) return 'All';
    return values.map(value => this.getOptionLabel(value, options)).join(', ');
  }

  private areAllOptionsSelected(values: string[], options: Array<string | { label: string; value: string }>): boolean {
    const optionValues = options.map(option => typeof option === 'string' ? option : option.value);
    return optionValues.length > 0 && optionValues.every(value => values.includes(value));
  }

  private getOptionLabel(value: string, options: Array<string | { label: string; value: string }>): string {
    const option = options.find(item => (typeof item === 'string' ? item : item.value) === value);
    return typeof option === 'string' ? option : option?.label ?? value;
  }

  getSectionLabel(value: string): string {
    return value || 'Select';
  }

  toggleEpOption(values: string[], value: string): void {
    const idx = values.indexOf(value);
    if (idx >= 0) values.splice(idx, 1); else values.push(value);
  }

  isEpChecked(values: string[], value: string): boolean {
    return values?.includes(value);
  }

  isSelectedBrandConfigured(): boolean {
    // Check if the selected brand has any rows with section names configured
    const rowsForBrand = this.rows.filter(row => 
      this.selectedBrand === 'default' || row.brandList?.includes(this.selectedBrand)
    );
    
    // Return true if at least one row has a section name
    return rowsForBrand.some(row => row.sectionName && row.sectionName.trim() !== '');
  }

  isSingleBrandSelect(row: TopicsRow): boolean {
    const type = row.snapshotType?.toLowerCase() ?? '';
    const label = (this.snapshotTypeOptions.find(o => o.value === row.snapshotType)?.label ?? row.snapshotType)?.toLowerCase() ?? '';
    return type === 'singlebrand' || label.includes('single brand') ||
           type === 'multimarket' || type === 'multiplemarkets' || label.includes('multiple markets');
  }

  isAllEpSelected(values: string[], options: { label: string; value: string }[]): boolean {
    return options.length > 0 && options.every(o => values.includes(o.value));
  }

  toggleSelectAllEp(values: string[], options: { label: string; value: string }[], i: number): void {
    if (this.isAllEpSelected(values, options)) {
      values.splice(0, values.length);
    } else {
      options.forEach(o => { if (!values.includes(o.value)) values.push(o.value); });
    }
    this.getBrandList(i);
  }

  isBrandListDisabled(row: TopicsRow, i: number): boolean {
    if (!row.sectionName) return true;
    const ep1Enabled = (this.rowEntryPoint1Options[i]?.length ?? 0) > 0;
    const ep2Enabled = !!this.rowEntryPoint2Enabled[i];
    const ep3Enabled = !!this.rowEntryPoint3Enabled[i];
    if (ep1Enabled && !row.entryPoint1?.length) return true;
    if (ep2Enabled && !row.entryPoint2?.length) return true;
    if (ep3Enabled && !row.entryPoint3?.length) return true;
    return false;
  }

  onBrandChange(row: TopicsRow, value: string): void {
    const isChecked = row.brandList.includes(value);
    if (!isChecked && row.brandList.length >= 6) {
      this._alertService.showAlert({ header: 'Warning', message: 'Maximum 6 brands can be selected.', show: true });
      return;
    }
    this.toggleEpOption(row.brandList, value);
  }

  onBrandRadioChange(row: TopicsRow, value: string): void {
    row.brandList = [value];
  }

  getBrandRankLabel(row: TopicsRow, value: string): string {
    const index = row.brandList.indexOf(value);
    return index >= 0 ? `${index + 1}` : '';
  }

  onEpChange(values: string[], value: string, i: number): void {
    this.toggleEpOption(values, value);
    this.getBrandList(i);
  }

  rowSectionOptions: { [i: number]: string[] } = {};
  rowSubSectionMap: { [i: number]: { [section: string]: string[] } } = {};
  rowEntryPoint1Options: { [i: number]: { label: string; value: string }[] } = {};
  rowEntryPoint2Options: { [i: number]: { label: string; value: string }[] } = {};
  rowEntryPoint3Options: { [i: number]: { label: string; value: string }[] } = {};
  rowEntryPoint2Enabled: { [i: number]: string } = {};
  rowEntryPoint3Enabled: { [i: number]: string } = {};
  rowLevel1Values: { [i: number]: string[] } = {};
  rowEntryPointData: { [i: number]: any[] } = {};
  rowBrandData: { [i: number]: any[] } = {};
  rowBrandOptions: { [i: number]: { label: string; value: string }[] } = {};
  crossTabConfig: CrossTabConfiguration[] = [];

  toggleDropdown(i: number): void {
    const willOpen = this.openDropdownIndex !== i;
    this.openDropdownIndex = willOpen ? i : -1;
    if (willOpen) { this.dropdownSearch[`${i}_snapshotType`] = ''; }
  }

  closeDropdown(i: number): void {
    if (this.openDropdownIndex === i) this.openDropdownIndex = -1;
  }

  getBrandList(i: number): void {
    const row = this.rows[i];
    const level1Values = this.rowLevel1Values[i] ?? [];
    const options = this.rowEntryPoint1Options[i] ?? [];

    const buildEntryPoint = (selectedValues: string[], level1: string | null): EntryPointConfig => {
      const epData: any[] = this.rowEntryPointData[i] ?? [];
      const matchingItem = epData.find((item: any) => item.Level1 === level1);
      const varCode: string | null = matchingItem?.VariableCode ?? null;      
      return {
        Level1: level1,
        Level2: [{
          Parent: level1 ?? '',
          MetricOptions: selectedValues.map(val => {
            const opt = options.find(o => o.value === val);
            const matchingItemInner = epData.find((item: any) => item.Level1 === level1 && item.MetricOption == val);
            return { Name: opt?.label ?? val, responseCode: matchingItemInner?.ResponseCode ?? "", isActive: true };
          })
        }],
        variableCode: varCode
      };
    };

    const config: CrossTabConfiguration = {
      EntryPoint1: row.entryPoint1?.length ? buildEntryPoint(row.entryPoint1, level1Values[0] ?? null) : null,
      EntryPoint2: row.entryPoint2?.length ? buildEntryPoint(row.entryPoint2, level1Values[1] ?? null) : null,
      EntryPoint3: row.entryPoint3?.length ? buildEntryPoint(row.entryPoint3, level1Values[2] ?? null) : null,
      clientName: this.clientDetails.clientDetails.client_name,
      mandatory_variable_mapping: this.mandatoryVariableMapping ?? null,
      dimension: this.dimensionList ?? null,
      studyName: this.clientDetails.clientDetails.selectedStudy ?? this.clientDetails.clientDetails.selectedStudy,
      isMandatoryLoadClicked: false,
      snapshotType: row.snapshotType
    };
    const existing = this.crossTabConfig.findIndex((_, idx) => idx === i);
    if (existing >= 0) {
      this.crossTabConfig[existing] = config;
    } else {
      this.crossTabConfig.push(config);
    }
    
    const requstObject = this.convertToSeparated(config);
    this.isLoading = true;
    this._configService
            .getSnapshotLeftPanelData(requstObject)
            .subscribe({
              next: (data: any) => {
                this.isLoading = false;
                if (data?.Data) {
                  const parsedData: any[] = JSON.parse(data.Data);
                  const brandData = parsedData.filter((item: any) => item.Level1 === 'Brands');
                  this.rowBrandData[i] = brandData;
                  const brandMetricOptions: string[] = Array.from(
                    new Set(brandData.map((item: any) => item.MetricOption).filter((v: any) => v != null))
                  );
                  this.rowBrandOptions[i] = brandMetricOptions.map((opt: string) => ({ label: opt, value: opt }));
                }
              },
              error: () => { this.isLoading = false; }
            });
  }

  private restoreBrandOptions(i: number, restoredLevel1Values: (string | null)[]): void {
  const row = this.rows[i];
  const epData: any[] = this.rowEntryPointData[i] ?? [];

  const buildEP = (selectedValues: string[], level1: string | null): EntryPointConfig | null => {
    if (!selectedValues?.length || !level1) return null;
    const matchingItem = epData.find((item: any) => item.Level1 === level1 && item.MetricOption == selectedValues[0]);
    return {
      Level1: level1,
      Level2: [{ Parent: level1, MetricOptions: selectedValues.map(val => ({ Name: val, responseCode: val, isActive: true })) }],
      variableCode: matchingItem?.VariableCode ?? null
    };
  };

  const singleConfig: CrossTabConfiguration[] = [{
    EntryPoint1: buildEP(row.entryPoint1, restoredLevel1Values[0]),
    EntryPoint2: buildEP(row.entryPoint2, restoredLevel1Values[1]),
    EntryPoint3: buildEP(row.entryPoint3, restoredLevel1Values[2]),
    clientName: this.clientDetails.clientDetails.client_name,
    mandatory_variable_mapping: this.mandatoryVariableMapping ?? null,
    dimension: this.dimensionList ?? null,
    studyName: this.clientDetails.clientDetails.selectedStudy,
    isMandatoryLoadClicked: false,
    snapshotType: row.snapshotType
  }];

  this._configService.getSnapshotLeftPanelData(singleConfig).subscribe({
    next: (data: any) => {
      if (data?.Data) {
        const parsedData: any[] = JSON.parse(data.Data);
        const brandMetricOptions: string[] = Array.from(
          new Set(
            parsedData
              .filter((item: any) => item.Level1 === 'Brands')
              .map((item: any) => item.MetricOption)
              .filter((v: any) => v != null)
          )
        );
        this.rowBrandOptions[i] = brandMetricOptions.map((opt: string) => ({ label: opt, value: opt }));
      }
    },
    error: () => {}
  });
}

  onSnapshotTypeChange(row: string, i: number, isRestore = false): void {
    this.openDropdownIndex = -1;
    if (!isRestore) {
      this.rows[i].sectionName = '';
      this.rows[i].subSectionName = [];
      this.rowSectionOptions[i] = [];
      this.rowSubSectionMap[i] = {};
    }
    this.clientDetails.topicsSelection.snapshotType = row;
    if (!isRestore) {
      this.clientDetails.topicsSelection.sectionName = '';
      this.clientDetails.topicsSelection.subSectionName = '';
    }
    this.isLoading = true;
    this._customConfigService.getCustomSnapshotWidgetLibrary({ClientName: this.clientDetails.clientDetails.client_name, SnapshotName: this.clientDetails.clientDetails.snapshotselected})
    .subscribe({
      next: (c) => {
        this.isLoading = false;
      if(c.Data){
        const snapshotData = JSON.parse(c.Data);
        const snapshotTypeLabel = row;
        const filteredTypes = snapshotData?.snapshotType?.filter((type: any) =>
          type.snapshotType_name === snapshotTypeLabel
        ) ?? [];
        const sections: string[] = [];
        const subSectionMap: { [section: string]: string[] } = {};

        filteredTypes.forEach((type: any) => {
          type.section?.forEach((section: any) => {
            const sectionName: string = section.section_name;
            if (!sections.includes(sectionName)) sections.push(sectionName);
            if (!subSectionMap[sectionName]) subSectionMap[sectionName] = [];
            section.subsection?.forEach((sub: any) => {
              const subName: string = sub.subsection_name;
              if (subName && subName !== 'NA' && !subSectionMap[sectionName].includes(subName)) subSectionMap[sectionName].push(subName);
            });
          });
        });

        this.rowSectionOptions[i] = sections;
        this.rowSubSectionMap[i] = subSectionMap;

        // If current sectionName is not in the new options, reset it
        if (!isRestore && this.rows[i].sectionName && !sections.includes(this.rows[i].sectionName)) {
          this.rows[i].sectionName = '';
          this.rows[i].subSectionName = [];
        }
        
        if (isRestore) {
          const currentRow = this.rows[i];
          currentRow.sectionName = currentRow.sectionName;
          currentRow.subSectionName = currentRow.subSectionName;
        }
      }
    },
      error: () => { this.isLoading = false; }
    });
  }

  getLabelForValue(value: string): string {
    if (!value) return 'Select';
    return this.snapshotTypeOptions.find(o => o.value === value)?.label ?? value;
  }

  getSelectedBrandLabel(): string {
    return this.brandOptions.find(o => o.value === this.selectedBrand)?.label ?? this.selectedBrand;
  }

  onSectionChange(value: string, i: number, isRestore = false, afterLoad?: () => void): void {
    if (!isRestore) {
      this.rows[i].sectionName = value;
      this.rows[i].subSectionName = [];
    }
    const hasSubSections = (this.rowSubSectionMap[i]?.[value] ?? []).length > 0;
    if (!hasSubSections) {
      this.onSectionSubsectionChange(i, afterLoad);
    }
  }

  onSubSectionChange(value: string, i: number, isRestore = false, afterLoad?: () => void): void {
    this.clientDetails.topicsSelection.subSectionName = value;
    this.onSectionSubsectionChange(i, afterLoad);
  }

  toggleSubSection(row: TopicsRow, value: string, i: number): void {
    const idx = row.subSectionName.indexOf(value);
    if (idx >= 0) {
      row.subSectionName.splice(idx, 1);
    } else {
      row.subSectionName.push(value);
    }
    this.onSubSectionChange(row.subSectionName[0] ?? '', i);
  }

  getSubSectionLabel(values: string[], options: string[] = []): string {
    return this.getSelectedValuesLabel(values, options);
  }

  getAvailableSubSections(row: TopicsRow, i: number): string[] {
    return this.rowSubSectionMap[i]?.[row.sectionName] ?? [];
  }

  get allSelected(): boolean {
    return this.rows.length > 0 && this.rows.every(r => r.selected);
  }
  onSectionSubsectionChange(i: number, afterLoad?: () => void): void {
  this.isLoading = true;
  this._configService.getSnapshotEntryPointData({ snapshot_name: this.clientDetails.clientDetails.snapshotselected, client_name: this.clientDetails.clientDetails.client_name, client_code: this.clientDetails.clientDetails.client_name})
  .subscribe({
    next: (c) => {
      this.isLoading = false;
    if(c.Data){
      const entryPointData = JSON.parse(c.Data);
      this.rowEntryPointData[i] = entryPointData;
      this.clientDetails.entryPointsDetails = entryPointData;

      // Brand options: filter where Level0 === 'Brands'
      const brandData = (entryPointData as any[]).filter((item: any) => item.Level0 === 'Brands');
      this.rowBrandData[i] = brandData;
      const brandMetricOptions: string[] = Array.from(
        new Set(brandData.map((item: any) => item.MetricOption).filter((v: any) => v != null))
      );
      this.rowBrandOptions[i] = brandMetricOptions.map((opt: string) => ({ label: opt, value: opt }));

      const uniqueLevel1: string[] = Array.from(
        new Set((entryPointData as any[]).map((item: any) => item.Level1).filter((v: any) => v != null && v !== ''))
      );

      this.rowLevel1Values[i] = uniqueLevel1;
      this.rowEntryPoint2Enabled[i] = uniqueLevel1.length >= 2 ? uniqueLevel1[1] : '';
      this.rowEntryPoint3Enabled[i] = uniqueLevel1.length >= 3 ? uniqueLevel1[2] : '';

      const getOptionsForLevel0 = (level1: string): { label: string; value: string }[] => {
        const metrics: string[] = Array.from(
          new Set(
            (entryPointData as any[])
              .filter((item: any) => item.Level1 === level1)
              .map((item: any) => item.MetricOption)
              .filter((v: any) => v != null)
          )
        );
        return metrics.map((opt: string) => ({ label: opt, value: opt }));
      };

      this.rowEntryPoint1Options[i] = uniqueLevel1.length >= 1 ? getOptionsForLevel0(uniqueLevel1[0]) : [];
      this.rowEntryPoint2Options[i] = uniqueLevel1.length >= 2 ? getOptionsForLevel0(uniqueLevel1[1]) : [];
      this.rowEntryPoint3Options[i] = uniqueLevel1.length >= 3 ? getOptionsForLevel0(uniqueLevel1[2]) : [];
      
      if (afterLoad) afterLoad();
    }
    },
    error: () => { this.isLoading = false; }
  });
}
  initialLoad(): void {
  let parameter1 = {
        study: this.clientDetails.clientDetails.selectedStudy,
        client_name: this.clientDetails.clientDetails.client_name,
        client_code: this.clientDetails.clientDetails.client_name,
        isBrandAnalysis: false,
            dataProces :null,
   dataSource :null,
   fileType :null,
   dataFormat :null,
   moduledetails :null
      };
      this.isLoading = true;
      this._configService
            .getGeneralConfigData(parameter1)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (data: any) => {
                this.isLoading = false;
                if (data.Data != '') {
                  let lsORisData = JSON.parse(data.Data);
                  this.mandatoryVariableMapping = lsORisData?.configData?.mandatory_variable_mapping ;
                  this.dimensionList=lsORisData?.configData?.dimension ?? [];
                  
                }
              },
              error: () => { this.isLoading = false; }
            })
  if (!this.snapshot_details?.snapshotType_details?.length) return;

  const uniqueTypes: string[] = [];
  this.snapshot_details.snapshotType_details.forEach((detail: any) => {
    if (!uniqueTypes.includes(detail.snapshot_type)) {
      uniqueTypes.push(detail.snapshot_type);
    }
  });

  this.snapshotTypeOptions = uniqueTypes.map((type: string) => ({
    label: type,
    value: type
      .split(' ')
      .map((word: string, i: number) =>
        i === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join('')
  }));


}




  selectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.rows.forEach(r => r.selected = checked);
  }

  addNewRow(): void {
    if (this.rows.length >= 200) {
      this._alertService.showAlert({ header: 'Warning', message: 'Maximum 200 rows allowed. Cannot add more rows.', show: true });
      return;
    }
    this.rows.push({
      rowId: crypto.randomUUID(),
      selected: false,
      snapshotType: '',
      sectionName: '',
      subSectionName: [],
      entryPoint1: [],
      entryPoint2: [],
      entryPoint3: [],
      dimension1: '',
      dimension2: '',
      dimension3: '',
      brandList: [],
    });
  }

  duplicateRow(index: number): void {
    if (this.rows.length >= 200) {
      this._alertService.showAlert({ header: 'Warning', message: 'Maximum 200 rows allowed. Cannot duplicate row.', show: true });
      return;
    }
    const original = this.rows[index];
    const copy: TopicsRow = {
    ...original,
    rowId: crypto.randomUUID(),
      entryPoint1: [...original.entryPoint1],
      entryPoint2: [...original.entryPoint2],
      entryPoint3: [...original.entryPoint3],
      brandList: [...original.brandList],
    };
    this.rows.splice(index + 1, 0, copy);
    const newIndex = index + 1;

    // Shift all per-row maps for rows that moved down by one
    for (let i = this.rows.length - 1; i > newIndex; i--) {
      this.rowSectionOptions[i]      = this.rowSectionOptions[i - 1];
      this.rowSubSectionMap[i]       = this.rowSubSectionMap[i - 1];
      this.rowEntryPoint1Options[i]  = this.rowEntryPoint1Options[i - 1];
      this.rowEntryPoint2Options[i]  = this.rowEntryPoint2Options[i - 1];
      this.rowEntryPoint3Options[i]  = this.rowEntryPoint3Options[i - 1];
      this.rowEntryPoint2Enabled[i]  = this.rowEntryPoint2Enabled[i - 1];
      this.rowEntryPoint3Enabled[i]  = this.rowEntryPoint3Enabled[i - 1];
      this.rowLevel1Values[i]        = this.rowLevel1Values[i - 1];
      this.rowEntryPointData[i]      = this.rowEntryPointData[i - 1];
      this.rowBrandData[i]           = this.rowBrandData[i - 1];
      this.rowBrandOptions[i]        = this.rowBrandOptions[i - 1];
    }

    // Copy source row's maps to the new index
    this.rowSectionOptions[newIndex]     = this.rowSectionOptions[index] ? [...this.rowSectionOptions[index]] : [];
    this.rowSubSectionMap[newIndex]      = this.rowSubSectionMap[index] ? { ...this.rowSubSectionMap[index] } : {};
    this.rowEntryPoint1Options[newIndex] = this.rowEntryPoint1Options[index] ? [...this.rowEntryPoint1Options[index]] : [];
    this.rowEntryPoint2Options[newIndex] = this.rowEntryPoint2Options[index] ? [...this.rowEntryPoint2Options[index]] : [];
    this.rowEntryPoint3Options[newIndex] = this.rowEntryPoint3Options[index] ? [...this.rowEntryPoint3Options[index]] : [];
    this.rowEntryPoint2Enabled[newIndex] = this.rowEntryPoint2Enabled[index] ?? '';
    this.rowEntryPoint3Enabled[newIndex] = this.rowEntryPoint3Enabled[index] ?? '';
    this.rowLevel1Values[newIndex]       = this.rowLevel1Values[index] ? [...this.rowLevel1Values[index]] : [];
    this.rowEntryPointData[newIndex]     = this.rowEntryPointData[index] ? [...this.rowEntryPointData[index]] : [];
    this.rowBrandData[newIndex]          = this.rowBrandData[index] ? [...this.rowBrandData[index]] : [];
    this.rowBrandOptions[newIndex]       = this.rowBrandOptions[index] ? [...this.rowBrandOptions[index]] : [];
  }

  deleteRows(): void {
    const selectedRows = this.rows.filter(r => r.selected);
    if (!selectedRows.length) {
      this._alertService.showAlert({ header: 'Warning', message: 'Please select at least one row to delete.', show: true });
      return;
    }

    const payload = {
      clientName: this.clientDetails.clientDetails.client_name,
      studyName: this.clientDetails.clientDetails.selectedStudy,
      snapshotName: this.clientDetails.clientDetails.snapshotselected,
      rowIds: selectedRows.map(r => r.rowId)
    };

    this.isLoading = true;
    this._configService.deleteTopicsConfiguration(payload).subscribe({
      next: () => {
        this.isLoading = false;
        this.rows = this.rows.filter(r => !r.selected);
        this._alertService.showAlert({ header: 'Success', message: 'Deleted Successfully', show: true });
      },
      error: () => {
        this.isLoading = false;
        this._alertService.showAlert({ header: 'Error', message: 'Error while deleting', show: true });
      }
    });
  }

  download(): void {
    // TODO: implement download
  }

  triggerUpload(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    // TODO: handle file upload
  }

  submit(): void {
    const buildEntryPoint = (
      i: number,
      selectedValues: string[],
      level1: string | null
    ): EntryPointConfig | null => {
      if (!selectedValues?.length) return null;
      const epData: any[] = this.rowEntryPointData[i] ?? [];
      const options = this.rowEntryPoint1Options[i] ?? [];
      const matchingItem = epData.find((item: any) => item.Level1 === level1);
      const varCode: string | null = matchingItem?.VariableCode ?? null;
      return {
        Level1: level1,
        Level2: [{
          Parent: level1 ?? '',
          MetricOptions: selectedValues.map(val => {
            const opt = options.find((o: any) => o.value === val);
            return { Name: opt?.label ?? val, responseCode: val, isActive: true };
          })
        }],
        variableCode: varCode
      };
    };

    const payloadRows = this.rows.map((row, i) => {
      const level1Values = this.rowLevel1Values[i] ?? [];
      return {
        rowId: row.rowId ?? crypto.randomUUID(),
        snapshotType: this.snapshotTypeOptions.find(o => o.value === row.snapshotType)?.label ?? row.snapshotType,
        sectionName: row.sectionName,
        subSectionName: row.subSectionName,
        brandList: row.brandList,
        dimension1: row.dimension1,
        dimension2: row.dimension2,
        dimension3: row.dimension3,
        EntryPoint1: buildEntryPoint(i, row.entryPoint1, level1Values[0] ?? null),
        EntryPoint2: buildEntryPoint(i, row.entryPoint2, level1Values[1] ?? null),
        EntryPoint3: buildEntryPoint(i, row.entryPoint3, level1Values[2] ?? null),
        mandatory_variable_mapping: this.mandatoryVariableMapping ?? null,
        dimension: this.dimensionList ?? null
      };
    });

    const uniquePayloads = new Set(
      payloadRows.map(r =>
        JSON.stringify({
          snapshotType: r.snapshotType,
          sectionName: r.sectionName,
          subSectionName: r.subSectionName,
          brandList: r.brandList,
          EntryPoint1: r.EntryPoint1,
          EntryPoint2: r.EntryPoint2,
          EntryPoint3: r.EntryPoint3
        })
      )
    );

    if (uniquePayloads.size !== payloadRows.length) {
      this._alertService.showAlert({ header: 'Error', message: 'Duplicate selection is not allowed', show: true });
      return;
    }

    const finalPayload = {
      data: {
        clientName: this.clientDetails.clientDetails.client_name,
        studyName: this.clientDetails.clientDetails.selectedStudy,
        snapshotName: this.clientDetails.clientDetails.snapshotselected,
        rows: payloadRows
      }
    };

 

    this._configService.addTopicsConfiguration(finalPayload).subscribe({
      next: (response: any) => {
        console.log(response);
        this._alertService.showAlert({ header: 'Success', message: 'Saved Successfully', show: true });
      },
      error: (error: any) => {
        console.error(error);
        this._alertService.showAlert({ header: 'Error', message: 'Error while saving', show: true });
      }
    });
  }

  downloadUserGuide(): void {
    // TODO: implement download user guide
  }

  convertToSeparated(input: any) {
  const sharedFields = ["clientName", "studyName", "mandatory_variable_mapping", "dimension"];
  const result = [];
  const entryPointKeys = Object.keys(input).filter(k => k.startsWith("EntryPoint") && input[k] !== null);

  for (const key of entryPointKeys) {
    const obj = {
      [key]: input[key]  
    };

    for (const field of sharedFields) {
      if (input[field] !== undefined) {
        obj[field] = input[field];
      }
    }
    result.push(obj);
  }

  return result;
}
}

